/** This is a local viewing preference; it never changes room state. */
export type DrawerTab='lost'|'accepted';
export function setLostDrawerOpen(root: HTMLElement, open: boolean, tab?:DrawerTab): void {
  const drawer=root.querySelector<HTMLElement>('.lost-card-drawer');
  if(!drawer)return;
  const selected=tab ?? (drawer.dataset.drawerTab==='accepted'?'accepted':'lost');
  const wasOpen=drawer.dataset.open==='true' && drawer.dataset.drawerTab===selected;
  if(!open && drawer.contains(document.activeElement))root.querySelector<HTMLElement>('.lost-drawer-toggle')?.focus({preventScroll:true});
  drawer.dataset.open=String(open);
  drawer.setAttribute('aria-hidden',String(!open));
  drawer.inert=!open;
  drawer.dataset.drawerTab=selected;
  for(const option of root.querySelectorAll<HTMLElement>('.lost-drawer-option'))option.setAttribute('aria-expanded',String(open && option.dataset.drawerTab===selected));
  for(const collection of drawer.querySelectorAll<HTMLElement>('.drawer-collection')){
    collection.hidden=collection.dataset.collection!==selected;collection.inert=collection.hidden;
  }
  const count=drawer.querySelectorAll(`.drawer-collection[data-collection="${selected}"] .table-hand-card`).length;
  drawer.dataset.lostRows=String(Math.max(1,Math.ceil(count/4)));
  drawer.dataset.lostDesktopRows=String(Math.max(1,Math.ceil(count/6)));
  root.querySelector<HTMLElement>('.lost-card-dock')?.setAttribute('data-drawer-open',String(open));
  if(open===wasOpen)return;
  const reduced=drawer.ownerDocument.defaultView?.matchMedia('(prefers-reduced-motion: reduce)').matches;
  for(const [index,card] of [...drawer.querySelectorAll<HTMLElement>(`.drawer-collection[data-collection="${selected}"] .lost-drawer-grid>.table-hand-card`)].entries()){
    card.getAnimations?.().filter(a=>a.id==='drawer-card-sway').forEach(a=>a.cancel());
    if(!open || reduced)continue;
    const direction=index%2===0?1:-1;
    const animation=card.animate?.([
      {translate:'0px 0px',rotate:'0deg'},
      {translate:`${-6*direction}px 0px`,rotate:`${-2.2*direction}deg`,offset:.25},
      {translate:`${2.8*direction}px 0px`,rotate:`${.95*direction}deg`,offset:.6},
      {translate:'0px 0px',rotate:'0deg'},
    ],{duration:500,delay:60+(index%4)*12,easing:'ease-out'});
    if(animation){
      animation.id='drawer-card-sway';
      // Closing or opening another card may cancel the decorative motion.
      void animation.finished.catch(()=>{});
    }
  }
}

export function dockTop(pointerY:number,height:number):number {
  return Math.max(12,Math.min(height-76,pointerY-32));
}

/** Pointer gestures and menu state stay on this device, outside game state. */
export class LostDrawerDock {
  private drawerLayout:{element:HTMLElement;key:string;height:number}|null=null;
  private viewport={width:window.innerWidth,height:window.innerHeight};
  private fraction=.62;
  private menu=false;
  private player:string|undefined;
  private pointer:{id:number;x:number;y:number;top:number;moved:boolean}|null=null;
  private suppressUntil=0;
  constructor(private readonly root:HTMLElement,private readonly onOpen:(open:boolean,tab?:DrawerTab)=>void){
    root.addEventListener('pointerdown',event=>{
      if(event.button!==0 || !(event.target as HTMLElement).closest('.lost-drawer-toggle'))return;
      const dock=root.querySelector<HTMLElement>('.lost-card-dock');if(!dock)return;
      this.pointer={id:event.pointerId,x:event.clientX,y:event.clientY,top:dock.getBoundingClientRect().top,moved:false};
    });
    root.addEventListener('pointermove',event=>{
      const pointer=this.pointer;if(!pointer || pointer.id!==event.pointerId)return;
      if(Math.hypot(event.clientX-pointer.x,event.clientY-pointer.y)>6)pointer.moved=true;
      if(!pointer.moved)return;
      if(!root.hasPointerCapture(event.pointerId))root.setPointerCapture(event.pointerId);
      event.preventDefault();this.menu=false;
      const top=dockTop(pointer.top+32+event.clientY-pointer.y,innerHeight);
      this.fraction=(top+32)/innerHeight;this.refresh();
    });
    const finish=(event:PointerEvent)=>{
      if(!this.pointer || event.pointerId!==this.pointer.id)return;
      const tap=!this.pointer.moved && event.type==='pointerup' && event.pointerType==='touch';
      if(this.pointer.moved || tap)this.suppressUntil=Date.now()+350;
      this.pointer=null;
      if(root.hasPointerCapture(event.pointerId))root.releasePointerCapture(event.pointerId);
      if(tap)this.toggleMenu();else this.refresh();
    };
    root.addEventListener('pointerup',finish);root.addEventListener('pointercancel',finish);
    root.addEventListener('click',event=>{
      const target=(event.target as HTMLElement).closest<HTMLElement>('[data-action]');
      const action=target?.dataset.action;
      if(!['lost-dock-menu','lost-dock-open','toggle-lost-drawer'].includes(action ?? '')){
        if(this.menu && !(event.target as HTMLElement).closest('.lost-card-dock')){this.menu=false;this.refresh();}
        return;
      }
      event.stopImmediatePropagation();
      if(action==='lost-dock-menu'){
        if(event.detail>0 && Date.now()<this.suppressUntil)return;
        this.toggleMenu();
      }else{
        const open=action==='lost-dock-open';this.menu=false;
        this.onOpen(open,open?(target?.dataset.drawerTab==='accepted'?'accepted':'lost'):undefined);this.refresh();
      }
    });
    root.addEventListener('keydown',event=>{
      if(event.key==='Escape' && this.menu){this.menu=false;this.refresh();}
      const button=(event.target as HTMLElement).closest('.lost-drawer-toggle');
      if(button && ['ArrowUp','ArrowDown','Home','End'].includes(event.key)){
        event.preventDefault();
        const top=dockTop(this.fraction*innerHeight,innerHeight);
        const target=event.key==='Home'?12:event.key==='End'?innerHeight-76:top+(event.key==='ArrowUp'?-32:32);
        this.fraction=(dockTop(target+32,innerHeight)+32)/innerHeight;this.refresh();
      }
    });
    window.addEventListener('resize',()=>{this.viewport={width:window.innerWidth,height:window.innerHeight};this.refresh();});
  }
  private toggleMenu():void {
    if(this.root.querySelector<HTMLElement>('.lost-card-drawer')?.dataset.open==='true'){
      this.menu=false;this.onOpen(false);
    }else this.menu=!this.menu;
    this.refresh();
  }
  refresh():void {
    const dock=this.root.querySelector<HTMLElement>('.lost-card-dock');
    const drawer=this.root.querySelector<HTMLElement>('.lost-card-drawer');
    if(!dock || !drawer){this.menu=false;return;}
    const player=this.root.querySelector<HTMLElement>('.tabletop')?.dataset.tablePlayer;
    if(this.player!==player){this.player=player;this.menu=false;}
    const height=this.viewport.height;
    const top=dockTop(this.fraction*height,height);
    dock.style.top=`${top}px`;
    dock.dataset.menuOpen=String(this.menu);
    dock.dataset.dragging=String(!!this.pointer?.moved);
    dock.dataset.drawerOpen=drawer.dataset.open;
    dock.querySelector('.lost-drawer-toggle')?.setAttribute('aria-expanded',String(this.menu));
    const menu=dock.querySelector<HTMLElement>('.lost-drawer-menu')!;
    menu.inert=!this.menu;menu.setAttribute('aria-hidden',String(!this.menu));
    // A closed drawer must not force layout of all its hidden card faces on
    // every draw/spectator message. Measure only when it is actually opened.
    if(drawer.dataset.open!=='true')return;
    const key=[this.viewport.width,height,drawer.dataset.drawerTab,drawer.dataset.lostRows,drawer.dataset.lostDesktopRows].join(':');
    if(this.drawerLayout?.element!==drawer || this.drawerLayout.key!==key)this.drawerLayout={element:drawer,key,height:drawer.offsetHeight};
    // Match the drawer's vertical origin to the draggable edge handle while
    // keeping every card within the available viewport.
    const drawerHeight=this.drawerLayout.height;
    const drawerTop=Math.max(12,Math.min(height-drawerHeight-12,top+32-drawerHeight/2));
    drawer.style.top=`${drawerTop}px`;
    drawer.style.setProperty('--drawer-origin-y',`${top+32-drawerTop}px`);
  }
}
